// GuidedTour.js — a lightweight, dependency-free first-run walkthrough.
//
// Mounted once in AppShell (inside the Router + UserProvider). Exposes startTour()
// via context so the welcome modal can launch it. Each step optionally spotlights a
// [data-tour="..."] element; if that anchor isn't on the page, the step falls back
// to a centered card, so the tour can never break a screen.

import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import tourSteps from "./tourSteps";
import "./GuidedTour.css";

const TourContext = createContext({ startTour: () => {} });
export const useTour = () => useContext(TourContext);

const CARD_W = 340;
const GAP = 14; // space between the spotlight and the card

export function TourProvider({ children }) {
  const [stepIndex, setStepIndex] = useState(null); // null = tour inactive
  const navigate = useNavigate();

  const startTour = useCallback(() => setStepIndex(0), []);
  const endTour = useCallback(() => setStepIndex(null), []);

  const value = { startTour };

  return (
    <TourContext.Provider value={value}>
      {children}
      {stepIndex !== null && (
        <TourOverlay
          stepIndex={stepIndex}
          setStepIndex={setStepIndex}
          endTour={endTour}
          navigate={navigate}
        />
      )}
    </TourContext.Provider>
  );
}

function TourOverlay({ stepIndex, setStepIndex, endTour, navigate }) {
  const step = tourSteps[stepIndex];
  const isLast = stepIndex === tourSteps.length - 1;
  const [rect, setRect] = useState(null); // anchor bounding box, or null = centered
  const rafRef = useRef(null);

  // On each step: navigate to its route, then poll briefly for the anchor element
  // (the route + its data needs a beat to render). Fall back to a centered card.
  useEffect(() => {
    setRect(null);
    if (step.route) navigate(step.route);

    if (!step.selector) return undefined;

    let tries = 0;
    let cancelled = false;
    const find = () => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        // Let the smooth-scroll settle before measuring.
        setTimeout(() => {
          if (!cancelled) setRect(el.getBoundingClientRect());
        }, 220);
        return;
      }
      if (tries++ < 25) {
        rafRef.current = setTimeout(find, 80); // ~2s of retries
      }
      // else: leave rect null → centered fallback
    };
    find();

    return () => {
      cancelled = true;
      if (rafRef.current) clearTimeout(rafRef.current);
    };
  }, [stepIndex, step.route, step.selector, navigate]);

  // Keep the spotlight aligned if the window resizes mid-step.
  useEffect(() => {
    if (!rect || !step.selector) return undefined;
    const onResize = () => {
      const el = document.querySelector(step.selector);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [rect, step.selector]);

  const next = () => (isLast ? endTour() : setStepIndex((i) => i + 1));
  const back = () => setStepIndex((i) => Math.max(0, i - 1));

  // Card position: anchored near the spotlight when we have a rect, else centered.
  const cardStyle = computeCardStyle(rect, step.placement);

  return (
    <div className="tour-root" role="dialog" aria-modal="true">
      {/* Click-blocker so the underlying UI isn't interacted with mid-tour. */}
      <div className="tour-blocker" />

      {rect ? (
        <div
          className="tour-spotlight"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
        />
      ) : (
        <div className="tour-dim" />
      )}

      <div className="tour-card" style={cardStyle}>
        <button className="tour-close" onClick={endTour} aria-label="Skip tour">×</button>
        <h3 className="tour-title">{step.title}</h3>
        <p className="tour-body">{step.body}</p>

        <div className="tour-dots">
          {tourSteps.map((_, i) => (
            <span key={i} className={`tour-dot ${i === stepIndex ? "active" : ""}`} />
          ))}
        </div>

        <div className="tour-actions">
          <button className="tour-skip" onClick={endTour}>Skip</button>
          <div className="tour-nav">
            {stepIndex > 0 && (
              <button className="tour-back" onClick={back}>Back</button>
            )}
            <button className="tour-next" onClick={next}>
              {isLast ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Position the card under (or above) the spotlight, clamped to the viewport. With
// no anchor, center it.
function computeCardStyle(rect, placement) {
  if (!rect) {
    return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = rect.left + rect.width / 2 - CARD_W / 2;
  left = Math.max(16, Math.min(left, vw - CARD_W - 16));

  const below = rect.bottom + GAP;
  const wantAbove = placement === "top" || below > vh - 180;
  const top = wantAbove
    ? Math.max(16, rect.top - GAP - 200)
    : below;

  return { top, left, width: CARD_W };
}

export default TourProvider;
